import {
	CreateManualMeasurementGroupRequest,
	ManualMeasurementPointGroup,
	UpdateManualMeasurementGroupRequest,
} from "@/components/Measurements/Types/MeasurementGroups";
import {
	ManualMeasurementPointRequest,
	ManualMeasurementPointResponse,
} from "@/components/Measurements/Types/MeasurementPoint";

import { DataAdapter } from "@/DataAdapter/DataAdapter";

import { ApplicationStatus } from "@/model/Application/ApplicationStatus";
import { GroupList } from "@/model/Application/GroupList";
import {
	CreateNewLanguageSettingsRequestType,
	LanguageSettingsRequestType,
	LanguageSettingsType,
} from "@/model/Application/LanguageSettings";
import { SearchResponse } from "@/model/Application/SearchResult";
import {
	BrandingSettingsType,
	GeneralFormData,
} from "@/model/Application/Settings/FormModels";
import {
	LabParameter,
	LabParameterRequest,
	LabSettings,
} from "@/model/Application/Settings/LabSettings";
import {
	BrandingSettingsRequest,
	CreateSystemSettingsRequest,
	OrganizationSettingsRequest,
	ProceduresSettingsRequestType,
	ServerEmailSettingsRequest,
	ServerPdfConversionSettingsRequest,
	ServerWordConvertorSettingsRequest,
	ShiftSettingsRequest,
	StartPageImageRequest,
	SystemSettingsResponse,
	TaskSettingsRequest,
} from "@/model/Application/Settings/RequestTypes";
import {
	ActiveDirectorySettings,
	AzureAdGroupSyncLogEntry,
	MeasurementsDaily,
	ProceduresSettings,
	ServerEmailSettingsResponse,
	ServerPdfConversionSettingsResponse,
	ServerWordConvertorSettingsResponse,
	ShiftName,
	ShiftNameRequest,
	ShiftSettingsResponse,
	ShiftSystem,
	ShiftSystemRequest,
	StartPageImageResponse,
	SystemInformation,
	TaskSettingsResponse,
} from "@/model/Application/Settings/SettingsResponseTypes";
import { Timezones } from "@/model/Application/Settings/Timezones";
import { ApplicationFeatures } from "@/model/ApplicationFeatures";
import { ArchiveUsersResponse } from "@/model/Archive/ArchiveUsersResponse";
import {
	ArchiveProject,
	ArchivedProjectsRequest,
	ArchivedProjectsResponse,
} from "@/model/Archive/ArchivedProjectsRequest";
import { Checklist } from "@/model/Checklist/Checklist";
import { ChecklistStepLog } from "@/model/Checklist/ChecklistStep";
import { Company } from "@/model/Company/Company";
import { CreateCompanyRequest } from "@/model/Company/CreateCompanyRequest";
import { CompetenceMatrix } from "@/model/Competence/CompetenceMatrix";
import { CompetenceUserResult } from "@/model/Competence/CompetenceUserResult";
import {
	ActivateCompetencyTestEmailRequest,
	ActivateCompetencyTestEmailResponse,
	ActiveCompetencyEmailJobConfiguration,
	CreateCompetencyEmailJobRequest,
	CreateCompetencyEmailJobResponse,
} from "@/model/Competence/CreateCompetencyEmailJobRequest";
import { CreateCompetencyRequest } from "@/model/Competence/CreateCompetencyRequest";
import { ProcedureUpdateNoticeRequest } from "@/model/Competence/ProcedureUpdateNoticeRequest";
import {
	CompanyCompetenceGroupResponse,
	UpdateUserCompetenceDocumentStatusRequest,
	UpdateUserCompetenceDocumentStatusResponse,
	UserCompetence,
	UserCompetenceDocumentStatusRequest,
	UserCompetenceDocumentStatusResponse,
	UserCompetenceRequest,
} from "@/model/Competence/UserCompetence";
import { UserProcedureKnowledge } from "@/model/Competence/UserProcedureKnowledge";
import {
	ResourceDeleteRequest,
	ResourceDeleteResponse,
	ResourceUpdateRequest,
} from "@/model/Competency/Competency";
import {
	Group,
	GroupRequestType,
	GroupResource,
	GroupResourceRequest,
	GroupResourceType,
	GroupResources,
} from "@/model/Group/Group";
import { CreateMaterialHandlingProcedure } from "@/model/MaterialHandling/CreateMaterialHandlingProcedure";
import { DeleteMaterialHandling } from "@/model/MaterialHandling/DeleteMaterialHandling";
import {
	MaterialHandlingApproveVersionRequest,
	MaterialHandlingApproveVersionResponse,
} from "@/model/MaterialHandling/MaterialHandlingApproveVersionRequest";
import { MaterialHandlingArchiveListItem } from "@/model/MaterialHandling/MaterialHandlingArchiveListItem";
import { MaterialHandlingEquipment } from "@/model/MaterialHandling/MaterialHandlingEquipment";
import { MaterialHandlingFastening } from "@/model/MaterialHandling/MaterialHandlingFastening";
import { MaterialHandlingList } from "@/model/MaterialHandling/MaterialHandlingList";
import { MaterialHandlingProcedure } from "@/model/MaterialHandling/MaterialHandlingProcedure";
import { MaterialHandlingReview } from "@/model/MaterialHandling/MaterialHandlingReview";
import { MaterialHandlingSearchResponse } from "@/model/MaterialHandling/MaterialHandlingSearchResponse";
import { MaterialHandlingSettings } from "@/model/MaterialHandling/MaterialHandlingSettings";
import { MaterialHandlingUnit } from "@/model/MaterialHandling/MaterialHandlingUnit";
import { NewMaterialHandlingProcedureReturn } from "@/model/MaterialHandling/NewMaterialHandlingProcedureReturn";
import { ReferenceUrl } from "@/model/MaterialHandling/ReferenceUrl";
import {
	OrganizationModules,
	OrganizationModulesRequest,
} from "@/model/Modules/OrganizationModules";
import { PagedResponse } from "@/model/PagedResponse";
import { ActiveProcedure } from "@/model/Procedure/ActiveProcedure";
import { CreateNewProcedureRequest } from "@/model/Procedure/CreateNewProcedure";
import { DeleteProcedureRequest } from "@/model/Procedure/DeleteProcedureRequest";
import { DocumentImportPlan } from "@/model/Procedure/DocumentImportPlan";
import { DocumentUpdateRequest } from "@/model/Procedure/DocumentUpdateRequest";
import { EditProcedure } from "@/model/Procedure/EditProcedure";
import { NodeVersionComparison } from "@/model/Procedure/NodeVersionComparison";
import { Procedure } from "@/model/Procedure/Procedure";
import { ProcedureAccordionMove } from "@/model/Procedure/ProcedureAccordionMove";
import { ProcedureDetails } from "@/model/Procedure/ProcedureDetail";
import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";
import { ProcedureMetaData } from "@/model/Procedure/ProcedureMetaData";
import {
	CreateNewProcedureNodeRequest,
	CreateNewProcedureNodeResponse,
	DeleteProcedureNodeRequest,
	DeleteProcedureNodeResponse,
	UploadProcedureNodeDocumentRequest,
	UploadProcedureNodeDocumentResponse,
} from "@/model/Procedure/ProcedureNodeDependency";
import {
	ProcedureNodeStepComment,
	SuggestionRequest,
	SuggestionResponse,
} from "@/model/Procedure/ProcedureNodeStepComment";
import {
	ConvetToManualChecklistRequest,
	DeleteProcedureChecklistRequest,
	DeleteProcedureChecklistResponse,
	QueryUpsertProcedureNodeSteps,
	StepStatus,
} from "@/model/Procedure/ProcedureNodeSteps";
import {
	SaveProcedureRequest,
	SaveProcedureResponse,
} from "@/model/Procedure/ProcedureSaveRequest";
import {
	DocumentForProcedureImport,
	DocumentFromProcedure,
	DocumentUsedForNodeResponse,
} from "@/model/Procedure/ProcedureUseDocument";
import {
	ImportProcedureNodeDocumentRequest,
	RefreshSharePointDocumentRequest,
	RefreshSharePointDocumentResponse,
	UpdateProcedureNodeDocumentResponse,
	UpdateProcedureNodeRequest,
	UpdateProcedureNodeResponse,
} from "@/model/Procedure/UpdateProcedureNodeRequest";
import { UpdateProcedureVersionRequest } from "@/model/Procedure/UpdateProcedureVersionRequest";
import { UpdateStepStatusRequest } from "@/model/Procedure/UpdateStepStatusRequest";
import { UpdateStepStatusResponse } from "@/model/Procedure/UpdateStepStatusResponse";
import { UppdatedNodeResponse } from "@/model/Procedure/UppdatedNodeResponse";
import { UppdatedProcedureVersion } from "@/model/Procedure/UppdatedProcedureVersion";
import {
	FileUpload,
	ResourceRemoteLocationRequest,
	ResourceRequestBase,
	ResourceResponse,
	ResourceUploadFileRequest,
} from "@/model/Resource/ResourceRequest";
import { OmniaDataPoints } from "@/model/SATOSLive/Omnia/OmniaDataPointResponse";
import { TimeSeriesResponse } from "@/model/SATOSLive/Omnia/TimeSeriesResponse";
import {
	CreateShiftEventCommentRequest,
	ShiftEventResponse,
	ShiftEventsQuery,
	ShiftOptions,
	UpdateShiftEventStatusRequest,
} from "@/model/Shift/Shift";
import { ShiftConfigurationResponse } from "@/model/Shift/ShiftConfiguration";
import { ShiftSummary } from "@/model/Shift/ShiftSummary";
import { JobStatusRequest, JobStatusResponse } from "@/model/Status/JobStatus";
import { Tag, UpdateTagsRequest } from "@/model/Tag/Tag";
import {
	SingleTaskRequest,
	SortDirection,
	TaskAddCommentRequest,
	TaskCommentResponse,
	TaskResponse,
	TaskSave,
} from "@/model/Task/TaskRequest";
import {
	StudentFailedReason,
	StudentFailedReasonRequest,
} from "@/model/Training/StudentFailedReason";
import {
	TrainingCourse,
	TrainingCourseRequest,
} from "@/model/Training/TrainingCourse";
import {
	TrainingDrill,
	TrainingDrillRequest,
} from "@/model/Training/TrainingDrill";
import {
	DrillSummary,
	TrainingDrillFolder,
	TrainingDrillFolderRequest,
} from "@/model/Training/TrainingDrillFolder";
import {
	CreateTrainingDrillSessionRequest,
	TrainingDrillSession,
	TrainingDrillSessionSummary,
	TrainingDrillSessionSummaryWithKpiValue,
	UpdateTrainingDrillSessionRequest,
} from "@/model/Training/TrainingDrillSession";
import {
	TrainingFile,
	TrainingFileRequest,
} from "@/model/Training/TrainingFile";
import {
	TrainingCourseSummary,
	TrainingFolder,
	TrainingFolderRequest,
} from "@/model/Training/TrainingFolder";
import {
	TrainingKpi,
	TrainingKpiRequest,
	TrainingKpiStatisticsQueryOptions,
} from "@/model/Training/TrainingKpi";
import { TrainingKpiStatistic } from "@/model/Training/TrainingKpiStatistic";
import {
	TrainingLink,
	TrainingLinkRequest,
} from "@/model/Training/TrainingLink";
import {
	CompleteTrainingSessionRequest,
	TrainingSession,
	TrainingSessionRequest,
	TrainingSessionSummary,
	TrainingSessionsQueryOptions,
} from "@/model/Training/TrainingSession";
import {
	GetUserGroupMembershipStatusResponse,
	GroupMembershipType,
} from "@/model/User/Employee";
import {
	ChangeUserOrganizationRequest,
	ChangeUserOrganizationResponse,
	User,
	UserCompaniesResponse,
	UserSummaryResponse,
} from "@/model/User/User";
import { UserSummary } from "@/model/User/UserSummary";
import { UserCompanyConfiguration } from "@/model/UserCompanyConfiguration/UserCompanyConfiguration";

import { TrainingUsersResponse } from "@/service/TrainingQueries";

export class SATOSPortal {
	static GetShiftGroups(): Promise<any[]> {
		return DataAdapter.Get<any[]>({
			endpoint: `Group/Shift`,
		});
	}

	static GetShiftNames(): Promise<ShiftName[]> {
		return DataAdapter.Get<ShiftName[]>({
			endpoint: `ShiftNames`,
		});
	}

	static GetShiftSummary(groupId: number) {
		return DataAdapter.Get<ShiftSummary>({
			endpoint: `Group/${groupId}/ShiftSummary`,
		});
	}

	static GetTaskSingle(groupId: number) {
		return DataAdapter.Get<any>({
			endpoint: `Tasks/${groupId}`,
		});
	}

	static GetTaskSingleRecurring(groupId: number) {
		return DataAdapter.Get<any>({
			endpoint: `Tasks/${groupId}?fetchMoreRecurring=true`,
		});
	}

	public static UpdateMeasurement(request: any) {
		return DataAdapter.Post<any>(`LabModule/updatemeasurement`, request);
	}

	static GetCoursesEducation(): Promise<any[]> {
		return DataAdapter.Get<any[]>({
			endpoint: `ELearningCourses`,
		});
	}

	static GetCourseEducationMedia(id: number): any {
		return DataAdapter.Get<any>({
			endpoint: `ELearningCourses/${id}/Media`,
		});
	}

	static GetCourseEducationQuestions(id: number): any {
		return DataAdapter.Get<any>({
			endpoint: `ELearningCourses/${id}/Questions`,
		});
	}

	static GetCourseExamResults(id: number): any {
		return DataAdapter.Get<any>({
			endpoint: `ELearningCourses/${id}/ExamResults`,
		});
	}
	static GetCourseExamResultsById(id: number): any {
		return DataAdapter.Get<any>({
			endpoint: `Exams/${id}`,
		});
	}
	static GetCourseEducationQuestion(id: number): any {
		return DataAdapter.Get<any>({
			endpoint: `ExamQuestions/${id}`,
		});
	}

	public static CreateShiftName(request: ShiftNameRequest) {
		return DataAdapter.Post<ShiftName>("ShiftNames", request);
	}

	public static CreateShiftCrew(request: any) {
		const name = request?.Name ?? request?.name ?? "";
		const qs = name ? `?name=${encodeURIComponent(name)}` : "";
		return DataAdapter.Post<any>(`ShiftCrew${qs}`, null);
	}

	public static UpdateShiftName(id: number, request: ShiftNameRequest) {
		return DataAdapter.Put<ShiftSystem>(`ShiftNames/${id}`, request);
	}

	public static UpdateShiftCrew(id: number, request: ShiftNameRequest) {
		const nameParam = request?.Name
			? `?name=${encodeURIComponent(request.Name)}`
			: "";
		return DataAdapter.Put<any>(`ShiftCrew/${id}${nameParam}`, null);
	}
	public static DeleteShiftName(id: number) {
		return DataAdapter.Delete(`ShiftNames/${id}`);
	}
	public static DeleteShiftCrew(id: number) {
		return DataAdapter.Delete(`ShiftCrew/${id}`);
	}

	public static GetShiftSystems(): Promise<ShiftSystem[]> {
		return DataAdapter.Get<ShiftSystem[]>({
			endpoint: `ShiftSystems`,
		});
	}
	public static GetShiftCrews(): Promise<any[]> {
		return DataAdapter.Get<any[]>({
			endpoint: `ShiftCrew`,
		});
	}
	public static GetMeasurementsDaily(): Promise<ShiftSystem[]> {
		return DataAdapter.Get<MeasurementsDaily[]>({
			endpoint: `LabModule/measurementdaily`,
		});
	}

	public static GetMeasurementsDailySaveClear(): Promise<ShiftSystem[]> {
		return DataAdapter.Get<MeasurementsDaily[]>({
			endpoint: `LabModule/measurementdailypage`,
		});
	}

	public static GetMeasurementsSpot(): Promise<ShiftSystem[]> {
		return DataAdapter.Get<MeasurementsDaily[]>({
			endpoint: `LabModule/measurementspot`,
		});
	}

	public static GetLabGroups(): Promise<[]> {
		return DataAdapter.Get<[]>({
			endpoint: `LabModule/labgroups`,
		});
	}

	public static GetLabSystems(): Promise<[]> {
		return DataAdapter.Get<[]>({
			endpoint: `LabModule/labpsystems`,
		});
	}

	public static GetLabParams(): Promise<[]> {
		return DataAdapter.Get<[]>({
			endpoint: `LabModule/labparameters`,
		});
	}

	public static GetLabReportPage(): Promise<[]> {
		return DataAdapter.Get<[]>({
			endpoint: `LabModule/measurementarchive/0`,
		});
	}

	static GetLabReportPageId(request: { id: number }): Promise<any> {
		return DataAdapter.Get<any>({
			endpoint: `LabModule/measurementarchive/${request.id}`,
		});
	}

	static GetMeasurementById(request: { id: number }): Promise<any> {
		return DataAdapter.Get<any>({
			endpoint: `LabModule/measurement/${request.id}`,
		});
	}

	public static SaveSearch(request: any) {
		return DataAdapter.Post<any>(`LabModule/measurements`, request);
	}

	public static UpdateLabGroup(request: any) {
		return DataAdapter.Post<any>(`LabModule/updatelabgroup`, request);
	}

	public static UpdateLabAnalyze(request: any) {
		return DataAdapter.Post<any>(`LabModule/updatelabanalyze`, request);
	}

	public static CreateTaskComment(comment: any): Promise<any> {
		return DataAdapter.PostForm(`TaskComments`, comment);
	}

	public static CreateTask(task: any): Promise<any> {
		return DataAdapter.PostFormObject(`Tasks`, task);
	}
	public static UpdateTask(taskId: number, payload: any): Promise<any> {
		return DataAdapter.PutFormObject(`Tasks/${taskId}`, payload);
	}

	public static UpdateMeasurementGroup(request: any) {
		return DataAdapter.Post<any>(`LabModule/updatemeasurementgroup`, request);
	}

	public static UpdateLearningCourse(request: any) {
		return DataAdapter.Put<any>(`ELearningCourses/${request.id}`, request);
	}

	public static AddLearningCourse(request: any) {
		return DataAdapter.Post<any>(`ELearningCourses`, request);
	}

	public static AddNewQuestionToCourse(request: any) {
		return DataAdapter.Post<any>(
			`ELearningCourses/${request.courseId}/Questions`,
			request,
		);
	}

	public static AddExam(request: any) {
		return DataAdapter.Post<any>(`Exams`, request);
	}

	public static AddPutExam(request: any) {
		return DataAdapter.Put<any>(`Exams/${request.Id}`, request);
	}

	public static GetLabReports(request: any) {
		return DataAdapter.Post<any>(`LabModule/getLabReports`, request);
	}

	public static PostLabArchive(request: any) {
		return DataAdapter.Post<any>(`LabModule/createarchive`, request);
	}

	static GetMeasurementAnalyzeById(request: { id: number }): Promise<any> {
		return DataAdapter.Get<any>({
			endpoint: `LabModule/measurementanalyze/${request.id}`,
		});
	}

	static GetMeasurementByGroupId(request: { id: number }): Promise<any> {
		return DataAdapter.Get<any>({
			endpoint: `LabModule/measurementgroup/${request.id}`,
		});
	}

	static GetLabAnalyzeListById(request: { id: number }): Promise<any> {
		return DataAdapter.Get<any>({
			endpoint: `LabModule/labanalyzelist/${request.id}`,
		});
	}

	static GetLabAnalyzeById(request: { id: number }): Promise<any> {
		return DataAdapter.Get<any>({
			endpoint: `LabModule/labanalyze/${request.id}`,
		});
	}

	static GetFetchLabGroupsMeasurementPage(request: {
		id: number;
	}): Promise<any> {
		return DataAdapter.Get<any>({
			endpoint: `LabModule/measurementpage/${request.id}`,
		});
	}

	public static CreateShiftSystem(request: ShiftSystemRequest) {
		return DataAdapter.Post<ShiftSystem>("ShiftSystems", request);
	}

	public static UpdateShiftSystem(id: number, request: ShiftSystemRequest) {
		return DataAdapter.Put<ShiftSystem>(`ShiftSystems/${id}`, request);
	}

	static DeleteShiftSystem(id: number) {
		return DataAdapter.Delete(`ShiftSystems/${id}`);
	}

	static GetShiftSettings(): Promise<ShiftSettingsResponse> {
		return DataAdapter.Get<ShiftSettingsResponse>({
			endpoint: `Settings/Organization/Shift`,
		});
	}

	static UpdateShiftSettings(
		request: ShiftSettingsRequest,
	): Promise<ShiftSettingsResponse> {
		return DataAdapter.Put<ShiftSettingsResponse>(
			"Settings/Organization/Shift",
			request,
		);
	}

	static GetShiftOptions(): Promise<ShiftOptions> {
		return DataAdapter.Get<ShiftOptions>({
			endpoint: `shift/events/create-new-options`,
		});
	}

	static GetTaskGroupsByStatus(statuses: string[]): Promise<TaskOptions> {
		const query = statuses
			.map((s) => `status=${encodeURIComponent(s)}`)
			.join("&");

		return DataAdapter.Get<TaskOptions>({
			endpoint: `Group/Task?${query}`,
		});
	}

	static GetTaskGroupsAPI(): Promise<TaskOptions> {
		return DataAdapter.Get<TaskOptions>({
			endpoint: `user/me/TaskGroups`,
		});
	}

	static GetShiftEvents(request: ShiftEventsQuery): Promise<any> {
		const endpoint = buildEndpointWithQueryParameters("ShiftEvents", request);
		return DataAdapter.Get<any>({ endpoint });
	}

	public static UpdateShiftEvent(
		shiftLogData: any,
		shiftEventId: number,
	): Promise<any> {
		return DataAdapter.PutFormObject<any>(
			`ShiftEvents/${shiftEventId}`,
			shiftLogData,
		);
	}

	public static CreateShiftEvent(shiftEvent: any): Promise<any> {
		return DataAdapter.PostFormObject<any>("ShiftEvents", shiftEvent);
	}

	public static UploadShiftEventFile(file: File): Promise<File> {
		return DataAdapter.Post<File>(`shift/events/file-upload`, file);
	}

	public static CopyUserSettings(request: {
		targetUser: number;
		sourceUser: number;
	}): Promise<void> {
		return DataAdapter.Post<void>(`Group/copygroups`, request);
	}

	public static CreateShiftEventComment(
		request: CreateShiftEventCommentRequest,
	): Promise<Comment> {
		return DataAdapter.Post<Comment>(`ShiftEventComments`, request);
	}

	public static DeleteShiftEventComment(commentId: number) {
		return DataAdapter.Delete(`ShiftEventComments/${commentId}`);
	}

	public static DeleteShiftEvent(shiftEventId: number) {
		return DataAdapter.Delete(`ShiftEvents/${shiftEventId}`);
	}

	public static UpdateGroupSaveClear(request: any): Promise<any> {
		return DataAdapter.Patch<any>(`Group/${request.groupId}`, request);
	}

	public static UpdateShiftEventStatus(
		request: UpdateShiftEventStatusRequest,
	): Promise<ShiftEventResponse> {
		return DataAdapter.Patch<ShiftEventResponse>(
			`ShiftEvents/${request.shiftId}`,
			request,
		);
	}

	static RefreshSharePointDocument(
		request: RefreshSharePointDocumentRequest,
	): Promise<RefreshSharePointDocumentResponse> {
		return DataAdapter.Post<RefreshSharePointDocumentResponse>(
			`documents/sharepoint/${request.ExternalUniqueId}`,
			request,
		);
	}

	static CreateNewProcedureNode(
		request: CreateNewProcedureNodeRequest,
	): Promise<CreateNewProcedureNodeResponse> {
		return DataAdapter.Post<CreateNewProcedureNodeResponse>(`nodes`, request);
	}

	static UploadProcedureNodeDocument(
		request: UploadProcedureNodeDocumentRequest,
	): Promise<UploadProcedureNodeDocumentResponse> {
		return DataAdapter.UploadForm(
			`nodes/${request.SchemeId}/document`,
			request,
		);
	}

	static GetNodeDocument(request: {
		ProcedureId: number;
		SchemeId: number;
	}): Promise<DocumentUsedForNodeResponse> {
		return DataAdapter.Get<DocumentUsedForNodeResponse>({
			endpoint: `Nodes/${request.SchemeId}/Document?ProcedureId=${request.ProcedureId}`,
		});
	}

	static UpdateProcedureNodeDocument(
		request: ImportProcedureNodeDocumentRequest,
	): Promise<UpdateProcedureNodeDocumentResponse> {
		return DataAdapter.PutForm<UpdateProcedureNodeDocumentResponse>(
			`nodes/${request.SchemeId}/document`,
			request,
		);
	}
	static UpdateProcedureNode(
		request: UpdateProcedureNodeRequest,
	): Promise<UpdateProcedureNodeResponse> {
		return DataAdapter.Put<UpdateProcedureNodeResponse>(
			`nodes/${request.SchemeId}`,
			request,
		);
	}

	static GetProcedureDocuments(
		searchTerm: string,
	): Promise<DocumentForProcedureImport[]> {
		const queryParam = searchTerm ? `?searchTerm=${searchTerm}` : "";
		return DataAdapter.Get<DocumentForProcedureImport[]>({
			endpoint: `Documents${queryParam}`,
		});
	}

	static GetActiveEmailJobs(): Promise<ActiveCompetencyEmailJobConfiguration> {
		return DataAdapter.Get<ActiveCompetencyEmailJobConfiguration>({
			endpoint: "competence/job/email/active",
		});
	}

	static ActivateCompetencyTestEmail(
		request: ActivateCompetencyTestEmailRequest,
	): Promise<ActivateCompetencyTestEmailResponse> {
		return DataAdapter.Post<ActivateCompetencyTestEmailResponse>(
			"competence/job/email/recipients",
			request,
		);
	}
	static ChangeUserOrganization(
		request: ChangeUserOrganizationRequest,
	): Promise<ChangeUserOrganizationResponse> {
		return DataAdapter.Put<ChangeUserOrganizationResponse>(
			"user/me/companies",
			request,
		);
	}
	static GetUserCompanies(): Promise<UserCompaniesResponse> {
		return DataAdapter.Get<UserCompaniesResponse>({
			endpoint: "user/me/companies",
		});
	}

	static GetUsers(companyId?: number): Promise<UserSummaryResponse[]> {
		const queryParam = companyId ? `?companyId=${companyId}` : "";
		return DataAdapter.Get<UserSummaryResponse[]>({
			endpoint: `user${queryParam}`,
		});
	}

	static GetUserSingle(userId: number): Promise<User> {
		return DataAdapter.Get<User>({
			endpoint: `user/${userId}`,
		});
	}

	static CreateCompetencyEmailJob(
		request: CreateCompetencyEmailJobRequest,
	): Promise<CreateCompetencyEmailJobResponse> {
		return DataAdapter.Post<CreateCompetencyEmailJobResponse>(
			"competence/job/email",
			request,
		);
	}
	static MaterialHandlingApproveVersionUpdate(
		request: MaterialHandlingApproveVersionRequest,
	): Promise<MaterialHandlingApproveVersionResponse> {
		return DataAdapter.Post<MaterialHandlingApproveVersionResponse>(
			"materialhandling/approve/version",
			request,
		);
	}
	static Search(searchTerm: string): Promise<SearchResponse> {
		return DataAdapter.Get<SearchResponse>({
			endpoint: `procedure/search/${searchTerm}`,
		});
	}
	static GetProcedureShiftConfiguration(): Promise<ShiftConfigurationResponse> {
		return DataAdapter.Get<ShiftConfigurationResponse>({
			endpoint: "procedure/shift/configuration",
		});
	}
	static GetProcedureStepLog(
		id: string,
		checklistId: number | undefined,
		stepId: number,
	): Promise<ChecklistStepLog> {
		return DataAdapter.Get<ChecklistStepLog>({
			endpoint: `ChecklistSteps/${stepId}/log`,
			queryParams: `?ProcedureId=${id}&ChecklistId=${checklistId}`,
		});
	}

	static GetProcedureSuggestionByChecklistId(
		checklistId: number | undefined,
	): Promise<any> {
		return DataAdapter.Get<any>({
			endpoint: `Checklist/${checklistId}/ProcedureUpdateSuggestions`,
		});
	}

	static GetCompetenceUserList(): Promise<CompetenceUserResult> {
		return DataAdapter.Get<CompetenceUserResult>({
			endpoint: "competence/list/user",
		});
	}

	static GetApplicationStatus(): Promise<ApplicationStatus> {
		return DataAdapter.Get<ApplicationStatus>({
			endpoint: `application`,
		});
	}

	static SearchOmniaByName(search: string): Promise<TimeSeriesResponse> {
		return DataAdapter.Get<TimeSeriesResponse>({
			endpoint: `omnia/search/${search}`,
		});
	}

	static GetAllOmniaTimeseries(search: string): Promise<TimeSeriesResponse> {
		return DataAdapter.Get<TimeSeriesResponse>({
			endpoint: `omnia/search/${search}`,
		});
	}

	static GetMultipleTagsCurrentValues(
		request: string[],
	): Promise<OmniaDataPoints[]> {
		return DataAdapter.Post<OmniaDataPoints[]>(
			"omnia/datapoints/multiple",
			request,
		);
	}

	static GetJobsStatus(request: JobStatusRequest): Promise<JobStatusResponse> {
		return DataAdapter.Post<JobStatusResponse>("status/jobs", request);
	}

	static AddTaskComment(
		request: TaskAddCommentRequest,
	): Promise<TaskCommentResponse> {
		return DataAdapter.UploadForm("v1/task/addcomment", request);
	}
	static SaveTask(request: TaskSave): Promise<number> {
		return DataAdapter.UploadForm("v1/task/save", request);
	}
	static GetTaskComments(
		request: SingleTaskRequest,
	): Promise<TaskCommentResponse> {
		return DataAdapter.Post("v1/task/comments", request);
	}

	static GetTask(request: SingleTaskRequest): Promise<TaskResponse> {
		return DataAdapter.Post<TaskResponse>("v1/task/", request);
	}
	static GetTasks(
		groupIds: number[],
		status: string,
		page: number,
		pageSize: number,
		searchTermTable?: string,
		sort?: SortDirection,
	): Promise<TaskResponse> {
		const query = new URLSearchParams();

		groupIds.forEach((id) => query.append("GroupIds", id.toString()));
		if (status) query.append("Status", status);
		if (searchTermTable) query.append("Keyword", searchTermTable);
		query.append("Page", page.toString());
		query.append("PageSize", pageSize.toString());
		if (sort) query.append("Sort", sort);

		return DataAdapter.Get<TaskResponse>({
			endpoint: `Tasks?${query.toString()}`,
		});
	}

	static GetProcedureUpdate(hasMaterialHandling: boolean) {
		return DataAdapter.Get<any>({
			endpoint: hasMaterialHandling
				? "procedureUpdateSuggestions/MaterialHandling"
				: "procedureUpdateSuggestions",
		});
	}

	static QueryMaterialHandlingProcedureGroups():
		| ProcedureGroup[]
		| Promise<ProcedureGroup[]> {
		return DataAdapter.Get<ProcedureGroup[]>({
			endpoint: "materialhandling/groups/",
		});
	}

	static SearchMaterialHandlingTag(
		searchTerm: string,
	): Promise<MaterialHandlingSearchResponse> {
		return DataAdapter.Get<MaterialHandlingSearchResponse>({
			endpoint: `materialhandling/search/${searchTerm}`,
		});
	}
	static GetCompanyCompetenceGroups(
		companyId: number | undefined,
	): Promise<CompanyCompetenceGroupResponse> {
		return DataAdapter.Get<CompanyCompetenceGroupResponse>({
			endpoint: `competence/company/${companyId}/groups`,
		});
	}
	static GetUserCompetenceDocumentStatus(
		request: UserCompetenceDocumentStatusRequest,
	): Promise<UserCompetenceDocumentStatusResponse> {
		return DataAdapter.Post<UserCompetenceDocumentStatusResponse>(
			"competence/user/document/status",
			request,
		);
	}

	static GetProcedureGroupDetails(
		id: number | undefined,
	): Promise<ProcedureGroup> {
		return DataAdapter.Get<ProcedureGroup>({
			endpoint: `proceduregroups/details/${id}`,
		});
	}

	public static GetCurrentResource(
		id: string | undefined,
	): Promise<ResourceResponse> {
		return DataAdapter.Get<ResourceResponse>({
			endpoint: `resource/download/${id}`,
		});
	}

	public static GetCurrentResourceDetails(
		id: string | undefined,
	): Promise<ResourceResponse> {
		return DataAdapter.Get<ResourceResponse>({
			endpoint: `resource/details/${id}`,
		});
	}

	public static GetCurrentResourceDetailsByFileId(
		request: ResourceRequestBase | undefined,
	): Promise<ResourceResponse> {
		return DataAdapter.Post<ResourceResponse>("resource/details/", request);
	}

	public static UpdateUserCompetenceUserDocumentStatus(
		request: UpdateUserCompetenceDocumentStatusRequest | undefined,
	): Promise<UpdateUserCompetenceDocumentStatusResponse> {
		return DataAdapter.Post<UpdateUserCompetenceDocumentStatusResponse>(
			"competence/user/document/update",
			request,
		);
	}

	public static GetCompetenceMatrix(id: string): Promise<CompetenceMatrix> {
		return DataAdapter.Get<CompetenceMatrix>({
			endpoint: `competence/competencematrix/${id}`,
		});
	}

	public static QueryArchiveUsers(): Promise<ArchiveUsersResponse> {
		return DataAdapter.Post<ArchiveUsersResponse>("archive/users", {});
	}

	public static QueryArchivedProcedure(
		procedureId: string,
	): Promise<ArchiveProject> {
		return DataAdapter.Get<ArchiveProject>({
			endpoint: `archive/completed/${procedureId}`,
		});
	}

	public static QueryArchivedProcedures(
		request: ArchivedProjectsRequest,
	): Promise<ArchivedProjectsResponse> {
		return DataAdapter.Post<ArchivedProjectsResponse>(
			"archive/project/list",
			request,
		);
	}

	public static CreateArchivedProcedurePdf(procedureId: number) {
		return DataAdapter.Get({
			endpoint: `archive/completed/${procedureId}/pdf`,
		});
	}

	public static User(): Promise<User> {
		return DataAdapter.Get<User>({ endpoint: "user/me" });
	}

	public static GetApplicationFeatures(): Promise<ApplicationFeatures> {
		return DataAdapter.Get({ endpoint: "Application/FeatureFlags" });
	}

	public static GetCompanies() {
		return DataAdapter.Get<Company[]>({
			endpoint: "Company",
		});
	}

	public static UserCompanyConfiguration() {
		return DataAdapter.Get<UserCompanyConfiguration>({
			endpoint: "usercompanyconfiguration",
		});
	}

	public static CreateOrganization(
		request: CreateCompanyRequest,
	): Promise<Company> {
		return DataAdapter.Post<Company>("Company", request);
	}

	public static GetActiveDirectorySettings() {
		return DataAdapter.Get<ActiveDirectorySettings>({
			endpoint: "settings/organization/activeDirectory",
		});
	}

	public static UpdateActiveDirectorySettings(
		activeDirectorySettings: ActiveDirectorySettings,
	) {
		return DataAdapter.Put<ActiveDirectorySettings>(
			"settings/organization/activeDirectory",
			activeDirectorySettings,
		);
	}

	public static GetOrganizationSettings() {
		return DataAdapter.Get<GeneralFormData>({
			endpoint: "settings/organization/general",
		});
	}

	public static UpdateOrganizationSettings(
		organizationSettings: OrganizationSettingsRequest,
	) {
		return DataAdapter.Put<OrganizationSettingsRequest>(
			"settings/organization/general",
			organizationSettings,
		);
	}

	public static GetSystemSettings() {
		return DataAdapter.Get<SystemSettingsResponse>({
			endpoint: "settings/system",
		});
	}

	public static CreateSystemSettings(request: CreateSystemSettingsRequest) {
		return DataAdapter.Post<SystemSettingsResponse>("settings/system", request);
	}

	public static GetAzureAdGroupSyncLogs() {
		return DataAdapter.Get<AzureAdGroupSyncLogEntry[]>({
			endpoint: "Settings/System/AzureAdGroupSyncLogs",
		});
	}

	public static GetLabParameters() {
		return DataAdapter.Get<LabParameter[]>({
			endpoint: "LabParameters",
		});
	}
	public static CreateLabParameter(request: LabParameterRequest) {
		return DataAdapter.Post<LabParameter>("LabParameters", request);
	}

	public static UpdateLabParameter(id: number, request: LabParameterRequest) {
		return DataAdapter.Put<LabParameter>(`LabParameters/${id}`, request);
	}

	public static DeleteLabParameter(id: number) {
		return DataAdapter.Delete(`LabParameters/${id}`);
	}

	public static GetLabSettings() {
		return DataAdapter.Get<LabSettings>({
			endpoint: "settings/organization/lab",
		});
	}

	public static UpdateLabSettings(request: LabSettings) {
		return DataAdapter.Put<LabSettings>("settings/organization/lab", request);
	}

	public static GetMaterialHandlingSettings() {
		return DataAdapter.Get<MaterialHandlingSettings>({
			endpoint: "settings/organization/MaterialHandling",
		});
	}

	public static UpdateMaterialHandlingSettings(
		setting: MaterialHandlingSettings,
	) {
		return DataAdapter.Put<MaterialHandlingSettings>(
			"settings/organization/MaterialHandling",
			setting,
		);
	}

	public static GetTimezones() {
		return DataAdapter.Get<Timezones[]>({
			endpoint: "settings/timezones",
		});
	}

	public static GetBrandingSettings() {
		return DataAdapter.Get<BrandingSettingsType>({
			endpoint: "settings/organization/Branding",
		});
	}

	public static UpdateBrandingSettings(
		brandingSettings: BrandingSettingsRequest,
	): Promise<BrandingSettingsType> {
		return DataAdapter.Put<BrandingSettingsType>(
			"settings/organization/Branding",
			brandingSettings,
		);
	}

	public static UploadStartPageImage(
		request: StartPageImageRequest,
	): Promise<StartPageImageResponse> {
		return DataAdapter.UploadImage<StartPageImageResponse>(
			"settings/Organization/Branding/StartPageImage",
			request,
		);
	}

	public static GetProceduresSettings() {
		return DataAdapter.Get<ProceduresSettings>({
			endpoint: "settings/organization/procedure",
		});
	}

	public static UpdateProcedureSettings(
		procedureSettings: ProceduresSettingsRequestType,
	): Promise<ProceduresSettingsRequestType> {
		return DataAdapter.Put<ProceduresSettingsRequestType>(
			"Settings/Organization/Procedure",
			procedureSettings,
		);
	}

	public static OrganizationModules(orgId: number) {
		return DataAdapter.Get<OrganizationModules>({
			endpoint: `organizationmodules/${orgId}`,
		});
	}

	public static UpdateOrganizationModules(
		request: OrganizationModulesRequest,
	): Promise<OrganizationModules> {
		return DataAdapter.Patch<OrganizationModules>(
			"organizationmodules",
			request,
		);
	}

	public static GetLanguageSettings(queryParams: string) {
		return DataAdapter.Get<LanguageSettingsType[]>({
			endpoint: "language/settings",
			queryParams: queryParams,
		});
	}

	public static UpdateLanguageSettings(
		languageSettings: LanguageSettingsRequestType,
	) {
		return DataAdapter.Put<LanguageSettingsRequestType>(
			`language/settings/${languageSettings.Id}`,
			languageSettings,
		);
	}

	public static CreateNewLanguageSettings(
		languageSettings: CreateNewLanguageSettingsRequestType,
	) {
		return DataAdapter.Post<CreateNewLanguageSettingsRequestType>(
			"language/settings",
			languageSettings,
		);
	}

	public static CreateLanguageSettingsBatch(
		languageSettings: CreateNewLanguageSettingsRequestType[],
	) {
		return DataAdapter.Post<CreateNewLanguageSettingsRequestType[]>(
			"language/Settings/Batch",
			languageSettings,
		);
	}

	public static GetTaskSettings() {
		return DataAdapter.Get<TaskSettingsResponse>({
			endpoint: "settings/organization/task",
		});
	}

	public static UpdateTaskSettings(request: TaskSettingsRequest) {
		return DataAdapter.Put<TaskSettingsRequest>(
			"settings/organization/task",
			request,
		);
	}

	public static GetServerEmailSettings() {
		return DataAdapter.Get<ServerEmailSettingsResponse>({
			endpoint: "settings/system/email",
		});
	}

	public static UpdateServerEmailSettings(request: ServerEmailSettingsRequest) {
		return DataAdapter.Put<ServerEmailSettingsRequest>(
			"settings/system/email",
			request,
		);
	}

	public static GetServerPdfConversionSettings() {
		return DataAdapter.Get<ServerPdfConversionSettingsResponse>({
			endpoint: "Settings/System/PdfConversion",
		});
	}

	public static UpdateServerPdfConversionSettings(
		request: ServerPdfConversionSettingsRequest,
	) {
		return DataAdapter.Put<ServerPdfConversionSettingsRequest>(
			"Settings/System/PdfConversion",
			request,
		);
	}

	public static GetServerWordConvertorSettings() {
		return DataAdapter.Get<ServerWordConvertorSettingsResponse>({
			endpoint: "Settings/System/WordConvertor",
		});
	}

	public static UpdateServerWordConvertorSettings(
		request: ServerWordConvertorSettingsRequest,
	) {
		return DataAdapter.Put<ServerWordConvertorSettingsRequest>(
			"Settings/System/WordConvertor",
			request,
		);
	}

	public static TriggerUserGroupMembershipUpdate(): Promise<number> {
		return DataAdapter.PutWithNoPayload("user/GroupMemberships");
	}

	public static GetSystemInformation() {
		return DataAdapter.Get<SystemInformation>({
			endpoint: "Settings/System/Information",
		});
	}

	public static GetGroups() {
		return DataAdapter.GetWithErrorThrowing<Group[]>({
			endpoint: "group",
		});
	}

	public static GetGroup(groupId: number): Promise<Group> {
		return DataAdapter.GetWithErrorThrowing<Group>({
			endpoint: `group/${groupId}`,
		});
	}

	public static GetUserGroupMembershipStatus(
		groupId: number,
	): Promise<GetUserGroupMembershipStatusResponse> {
		return DataAdapter.Get<GetUserGroupMembershipStatusResponse>({
			endpoint: `group/${groupId}/membership`,
		});
	}

	public static CreateGroup(group: GroupRequestType): Promise<Group> {
		return DataAdapter.Post<Group>(`group`, group);
	}

	public static UpdateGroup(group: GroupRequestType, id: number) {
		return DataAdapter.Put<Group>(`group/${id}`, group);
	}

	public static UpdateUser(user: any, id: number) {
		return DataAdapter.Patch(`user/${id}`, user);
	}
	public static DeleteGroup(groupId: number) {
		return DataAdapter.Delete(`group/${groupId}`);
	}

	public static UpdateGroupMembership(
		request: GroupMembershipType,
		userId: number,
		groupId: number,
	) {
		return DataAdapter.Put<GroupMembershipType>(
			`user/${userId}/groups/${groupId}/membershipType`,
			request,
		);
	}

	public static GetGroupResources(groupId: number): Promise<GroupResources> {
		return DataAdapter.GetWithErrorThrowing<GroupResources>({
			endpoint: `group/${groupId}/resources`,
		});
	}

	public static updateGroupResource(
		request: GroupResourceRequest,
		groupId: number,
		resourceType: GroupResourceType,
	) {
		return DataAdapter.Patch<GroupResource>(
			`group/${groupId}/resources/${resourceType}/${request.Id}`,
			request,
		);
	}

	public static updateTargetTime(request: any, ChecklistId: number) {
		return DataAdapter.Put<GroupResource>(
			`checklist/${ChecklistId}/targetDuration/`,
			request,
		);
	}

	public static GetProcedure(id: number): Promise<Procedure> {
		return DataAdapter.Get<Procedure>({
			endpoint: `procedure/${id}`,
		});
	}

	public static GetNode(
		procedureId: number,
		nodeId: number,
	): Promise<ProcedureDetails> {
		return DataAdapter.Get<ProcedureDetails>({
			endpoint: `nodes/${nodeId}`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static GetProcedureDetails<ProcedureDetails>(
		procedureId: string | number,
	): Promise<ProcedureDetails> {
		return DataAdapter.Get<ProcedureDetails>({
			endpoint: `procedure/${procedureId}/details`,
		});
	}

	public static GetCommentsByChecklistId(
		procedureId: number,
		checklistId: number,
	): Promise<Record<number, ProcedureNodeStepComment[]>> {
		return DataAdapter.Get<Record<number, ProcedureNodeStepComment[]>>({
			endpoint: `checklist/${checklistId}/comments`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static GetProcedureGroups(filter: string): Promise<ProcedureGroup[]> {
		return DataAdapter.Get<Array<ProcedureGroup>>({
			endpoint: "proceduregroups",
			odataParams: filter,
		});
	}

	public static GetProcedures(filter: string): Promise<Procedure[]> {
		return DataAdapter.Get<Array<Procedure>>({
			endpoint: "procedures",
			odataParams: filter,
		});
	}

	public static GetActiveProcedures(
		page: number,
		pageSize: number,
		search?: string,
		sort?: "ASC" | "DESC",
	) {
		const query = new URLSearchParams({
			Page: page.toString(),
			PageSize: pageSize.toString(),
		});

		if (search) {
			query.append("Search", search);
		}
		if (sort) {
			query.append("Sort", sort);
		}

		return DataAdapter.Get<PagedResponse<ActiveProcedure>>({
			endpoint: "procedure/active",
			queryParams: `?${query.toString()}`,
		});
	}

	public static GetMaterialHandlingChecklistForEdit(
		procedureId: number,
		checklistId: number | undefined,
	): Promise<Checklist> {
		return DataAdapter.Get<Checklist>({
			endpoint: `checklist/${checklistId}/MaterialHandlingForEdit`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static GetReviewChecklist(
		procedureId: number | undefined,
		isUpdatedChecklist: boolean,
		nodeId: number | undefined,
		checklistId: number | undefined,
	): Promise<Checklist> {
		return DataAdapter.Get<Checklist>({
			endpoint: `checklist/${checklistId}/review`,
			queryParams: `?ProcedureId=${procedureId}&NodeId=${nodeId}&IsUpdatedChecklist=${isUpdatedChecklist}`,
		});
	}

	public static GetChecklistStepStatuses(
		procedureId: number,
		checklistId: number | string | undefined,
	): Promise<StepStatus[]> {
		return DataAdapter.Get<StepStatus[]>({
			endpoint: `checklist/${checklistId}/stepStatuses`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static GetChecklist(
		procedureId: number | string | undefined,
		checklistId: number | string | undefined,
	): Promise<Checklist> {
		return DataAdapter.Get<Checklist>({
			endpoint: `checklist/${checklistId}`,
			queryParams: `?ProcedureId=${procedureId}`,
		});
	}

	public static UpsertChecklist(
		data: QueryUpsertProcedureNodeSteps,
	): Promise<QueryUpsertProcedureNodeSteps> {
		return DataAdapter.Put<QueryUpsertProcedureNodeSteps>(
			`checklist/${data.ChecklistId}`,
			data,
		);
	}

	public static GetProcedureMetaData(
		filter: string,
		signal: AbortSignal,
	): Promise<ProcedureMetaData> {
		return DataAdapter.Get<ProcedureMetaData>({
			endpoint: "ProceduresMetadata",
			odataParams: filter,
			signal: signal,
		});
	}

	public static GetProcedureMetaDataFolders(
		companyId: number | undefined,
	): Promise<ProcedureMetaData> {
		return DataAdapter.Get<ProcedureMetaData>({
			endpoint: "newproceduresmetadata",
			queryParams: `${companyId}/folders`,
		});
	}

	public static CreateProcedure(
		procedure: CreateNewProcedureRequest,
	): Promise<Procedure> {
		return DataAdapter.Post<Procedure>("procedure", procedure);
	}

	public static UpdateNodeTreePosition(
		request: ProcedureAccordionMove,
	): Promise<number> {
		return DataAdapter.Put<number>(
			`nodes/${request.SchemeId}/TreePosition`,
			request,
		);
	}

	public static UpdateProcedureSetting(
		procedureSetting: EditProcedure,
	): Promise<number> {
		if (procedureSetting.File !== null) {
			return DataAdapter.UploadForm<number>(
				"procedure/SettingsWithFile",
				procedureSetting,
			);
		}

		return DataAdapter.Post<number>("procedure/Settings", procedureSetting);
	}

	public static async DeleteProcedure(request: DeleteProcedureRequest) {
		return DataAdapter.Delete(`procedure/${request.procedureId}`);
	}

	public static MergeFileLinks(FileId: number): Promise<number> {
		return DataAdapter.Post<number>("procedure/mergefilelinks", FileId);
	}

	public static GetProcedureKnowledgeStatus(
		procedureId: number | undefined,
	): Promise<UserProcedureKnowledge> {
		return DataAdapter.Get({
			endpoint: "Competence",
			queryParams: `getprocedureknowledgestatus/${procedureId}`,
		});
	}

	public static PostProcedureUpdateNotice(
		updateNotice: ProcedureUpdateNoticeRequest,
	): Promise<any> {
		{
			return DataAdapter.Post<any>(
				"Competence/setprocedureupdatenotice",
				updateNotice,
			);
		}
	}

	public static PostProcedureKnowledgeStatus(
		procedureKnowledge: any,
	): Promise<any> {
		{
			return DataAdapter.Post<any>(
				"Competence/setnewprocedureknowledge",
				procedureKnowledge,
			);
		}
	}

	public static SaveAndResetProcedure(
		save: SaveProcedureRequest,
	): Promise<SaveProcedureResponse> {
		return DataAdapter.Post<SaveProcedureResponse>(
			`procedure/${save.ProcedureId}/save`,
			save,
		);
	}

	public static UpdateStepStatus(
		stepStatusUpdate: UpdateStepStatusRequest,
	): Promise<UpdateStepStatusResponse> {
		if (stepStatusUpdate.File !== null && stepStatusUpdate.File !== undefined) {
			return DataAdapter.UploadFormPatch<UpdateStepStatusResponse>(
				`ChecklistSteps/${stepStatusUpdate.StepId}/StatusWithFile`,
				stepStatusUpdate,
			);
		}

		return DataAdapter.Patch<UpdateStepStatusResponse>(
			`ChecklistSteps/${stepStatusUpdate.StepId}/Status`,
			stepStatusUpdate,
		);
	}

	public static PostProcedureUpdateSuggestion(
		Suggestion: any,
	): Promise<SuggestionResponse> {
		if (Suggestion.Attachment !== null) {
			return DataAdapter.UploadForm<SuggestionResponse>(
				"ProcedureUpdateSuggestions",
				Suggestion,
			);
		}

		return DataAdapter.Post<SuggestionResponse>(
			"ProcedureUpdateSuggestions",
			Suggestion,
		);
	}

	public static PutProcedureUpdateSuggestion(
		SuggestionId: number,
		Suggestion: Suggestion,
	): Promise<SuggestionRequest> {
		if (Suggestion.Attachment !== null) {
			return DataAdapter.PutForm<Suggestion>(
				`ProcedureUpdateSuggestions/${SuggestionId}`,
				Suggestion,
			);
		}

		return DataAdapter.PutForm<Suggestion>(
			`ProcedureUpdateSuggestions/${SuggestionId}`,
			Suggestion,
		);
	}

	public static AddProcedureUpdateSuggestionComment(
		Comment: any,
	): Promise<any> {
		return DataAdapter.Post<Suggestion>(
			`ProcedureUpdateSuggestions/addcommeent`,
			Comment,
		);
	}

	public static DeleteProcedureUpdateSuggestionComment(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`ProcedureUpdateSuggestions/deletecomment/${id}`,
		);
	}

	public static DeleteLabMeasurement(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`LabModule/labmeasurement/${id}`,
		);
	}

	public static DeleteLabGroup(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`LabModule/deletelabgroup/${id}`,
		);
	}

	public static DeleteTaskEvent(id: number) {
		return DataAdapter.Delete<any>(`Tasks/${id}`);
	}

	public static DeleteTaskEventComment(commentId: number, taskId: number) {
		const queryString = `TaskComments/${commentId}?taskId=${taskId}`;
		return DataAdapter.Delete<any>(queryString);
	}

	public static CompleteTaskEvent(id: number, payload: any) {
		return DataAdapter.Post<any>(`Tasks/${id}/Complete`, payload);
	}

	public static DeleteLabAnalyze(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`LabModule/labanalyze/${id}`,
		);
	}

	public static DeleteCourse(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`ELearningCourses/${id}`,
		);
	}

	public static AddMedia(courseId: number, payload: any) {
		return DataAdapter.AddUploadFormMedia<any>(
			`ELearningCourses/${courseId}/Media`,
			payload,
		);
	}

	public static UpdateMedia(mediaId: number, payload: FormData) {
		return DataAdapter.PutUploadFormMedia<any>(
			`ELearningMedia/${mediaId}`,
			payload,
		);
	}

	public static UpdateExamQuestion(payload: FormData) {
		const { id, ...restPayload } = payload;
		return DataAdapter.Put<any>(`ExamQuestions/${id}`, restPayload);
	}

	public static DeleteMedia(id: number) {
		return DataAdapter.Delete<any>(`ELearningMedia/${id}`);
	}

	public static DeleteQuestion(id: number) {
		return DataAdapter.Delete<any>(`ExamQuestions/${id}`);
	}

	public static UpdateProcedureGroup(save: ProcedureGroup): Promise<number> {
		return DataAdapter.Post<number>(`proceduregroups/update`, save);
	}

	public static UpdateProcedureFolder(
		request: ProcedureGroup,
	): Promise<ProcedureGroup> {
		return DataAdapter.Put("proceduregroups/update/new", request);
	}

	public static GetMaterialHandlingProcedure(
		MaterialHandlingid: number | undefined,
		MaterialHandlingTagId: string,
	): Promise<MaterialHandlingProcedure> {
		return DataAdapter.Get<MaterialHandlingProcedure>({
			endpoint: "MaterialHandling",
			queryParams: `get/${MaterialHandlingid}/tagid/${MaterialHandlingTagId}`,
		});
	}

	public static GetMaterialHandlingProcedureForEdit(
		MaterialHandlingid: number | undefined,
		MaterialHandlingTagId: string,
	): Promise<MaterialHandlingProcedure> {
		return DataAdapter.Get<MaterialHandlingProcedure>({
			endpoint: "MaterialHandling",
			queryParams: `edit/${MaterialHandlingid}/tagid/${MaterialHandlingTagId}`,
		});
	}

	public static GetMaterialHandlingList(): Promise<MaterialHandlingList> {
		return DataAdapter.Get<MaterialHandlingList>({
			endpoint: "MaterialHandling",
			queryParams: `list`,
		});
	}

	public static GetMaterialHandlingListWaitingForApproval(): Promise<MaterialHandlingReview> {
		return DataAdapter.Get<MaterialHandlingReview>({
			endpoint: "MaterialHandling",
			queryParams: `list/approve`,
		});
	}

	public static GetMaterialHandlingArciveListItem(
		id: number,
	): Promise<MaterialHandlingArchiveListItem> {
		return DataAdapter.Get<MaterialHandlingArchiveListItem>({
			endpoint: "MaterialHandling",
			queryParams: `listarciveitem/${id}`,
		});
	}

	public static Updatematerialhandling(
		materialhandling: MaterialHandlingProcedure,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandling/PostMaterialHandlingProcedure",
			materialhandling,
		);
	}

	public static UpdatematerialhandlingV2(
		materialhandling: MaterialHandlingProcedure,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"materialhandling/procedure/update",
			materialhandling,
		);
	}

	public static ImportMaterialHandlingDockument(
		materialhandlingid: number,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"materialhandling/procedure/import",
			materialhandlingid,
		);
	}

	public static Addmaterialhandling(
		materialhandling: CreateMaterialHandlingProcedure,
	): Promise<NewMaterialHandlingProcedureReturn> {
		if (materialhandling.File != null) {
			return DataAdapter.UploadForm<NewMaterialHandlingProcedureReturn>(
				"MaterialHandling/AddMaterialHandlingProcedurewithfile",
				materialhandling,
			);
		} else {
			return DataAdapter.Post<NewMaterialHandlingProcedureReturn>(
				"MaterialHandling/AddMaterialHandlingProcedure",
				materialhandling,
			);
		}
	}

	public static AddNewUnit(newunit: MaterialHandlingUnit): Promise<number> {
		return DataAdapter.Post<number>("MaterialHandling/AddNewUnit", newunit);
	}

	public static AddNewFastening(
		newfastening: MaterialHandlingFastening,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandling/AddNewFastening",
			newfastening,
		);
	}

	public static AddNewEquipment(
		newequipment: MaterialHandlingEquipment,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandling/AddNewEquipment",
			newequipment,
		);
	}

	public static AddNewReference(newreference: ReferenceUrl): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandling/AddNewReferenceUrl",
			newreference,
		);
	}

	public static DeleteMaterialhandingElement(
		itemtodelete: DeleteMaterialHandling,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"MaterialHandling/deleteelement",
			itemtodelete,
		);
	}

	public static GetUserGroups(): Promise<GroupList> {
		return DataAdapter.Get<GroupList>({
			endpoint: "MaterialHandling",
			queryParams: `GetUserGroups`,
		});
	}

	public static SaveAndResetMaterialHandling(
		save: SaveProcedureRequest,
	): Promise<SaveProcedureResponse> {
		return DataAdapter.Post<SaveProcedureResponse>(
			`MaterialHandling/${save.ProcedureId}/save`,
			save,
		);
	}

	public static GetUserCompetency(fetchUserCompetence: UserCompetenceRequest) {
		return DataAdapter.Post<UserCompetence>(
			"Competence/UserCompetence",
			fetchUserCompetence,
		);
	}

	public static DownloadCompetencyResource(
		request: ResourceRemoteLocationRequest,
	) {
		return DataAdapter.Post<ResourceResponse>(
			"resource/upload/remote",
			request,
		);
	}

	public static LinkCompetencyResource(request: ResourceRemoteLocationRequest) {
		return DataAdapter.Post<ResourceResponse>("resource/link/remote", request);
	}

	public static UploadFileResource(request: ResourceUploadFileRequest) {
		return DataAdapter.UploadForm<ResourceResponse>(
			"resource/upload/",
			request,
		);
	}

	public static UpdateCompetencyResource(request: ResourceUpdateRequest) {
		return DataAdapter.Post<ResourceResponse>("resource/update", request);
	}

	public static DeleteCompetencyResource(request: ResourceDeleteRequest) {
		return DataAdapter.Delete<ResourceDeleteResponse>(
			"resource/delete",
			request,
		);
	}

	public static DeleteProcedureComment(id: number) {
		return DataAdapter.Delete<DeleteProcedureChecklistResponse>(
			`StepComments/${id}`,
		);
	}

	public static CreateNewCompetencySave(
		request: CreateCompetencyRequest,
	): Promise<number> {
		return DataAdapter.Post<number>(
			"Competence/createnewcompetencysave",
			request,
		);
	}

	public static DeleteProcedureChecklist(
		request: DeleteProcedureChecklistRequest,
	) {
		return DataAdapter.Delete<boolean>(
			`checklist/${request.SchemeId}?procedureId=${request.ProcedureId}`,
		);
	}

	public static ConvertToManualChecklist(
		request: ConvetToManualChecklistRequest,
	) {
		return DataAdapter.Post<ConvetToManualChecklistRequest>(
			`checklist/${request.SchemeId}/ConvertToManual`,
			request,
		);
	}

	public static DeleteProcedureNode(request: DeleteProcedureNodeRequest) {
		return DataAdapter.Delete<DeleteProcedureNodeResponse>(
			`nodes/${request.SchemeId}`,
			request,
		);
	}

	public static GetDocumentsUsed(): Promise<DocumentFromProcedure[]> {
		return DataAdapter.Get<Array<DocumentFromProcedure>>({
			endpoint: "procedure/getdocumentsused",
		});
	}

	public static GetUppdatedProcedures(): Promise<UppdatedProcedureVersion[]> {
		return DataAdapter.Get<Array<UppdatedProcedureVersion>>({
			endpoint: "procedure/getuppdatedprocedures",
		});
	}

	public static UpdateNodeDocumentVersion(
		request: UpdateProcedureVersionRequest,
	) {
		return DataAdapter.Put<ResourceResponse>(
			`Nodes/${request.NodeSchemeId}/Version`,
			request,
		);
	}

	public static GetImportPlan(
		nodeId: number,
		checklistId: number,
	): Promise<DocumentImportPlan> {
		return DataAdapter.Get<DocumentImportPlan>({
			endpoint: `nodes/${nodeId}/checklists/${checklistId}/importPlan`,
		});
	}

	public static GetNewImportPlan(nodeId: number): Promise<DocumentImportPlan> {
		return DataAdapter.Get<DocumentImportPlan>({
			endpoint: `nodes/${nodeId}/importPlan`,
		});
	}

	public static ImportPlanFromWizard(
		request: DocumentImportPlan | undefined,
	): Promise<UppdatedNodeResponse> {
		return DataAdapter.Post<UppdatedNodeResponse>(
			`nodes/${request?.NodeId}/importPlanFromWizard`,
			request,
		);
	}

	public static ReimportDocument(request: DocumentUpdateRequest | undefined) {
		return DataAdapter.PutWithNoResponseBody(
			`documents/${request?.FileId}`,
			request,
		);
	}

	public static UpdateSharepointDocuments() {
		return DataAdapter.PutWithNoResponseBody("Documents/Sharepoint", {});
	}

	public static GetTags(): Promise<Tag[]> {
		return DataAdapter.Get<Tag[]>({
			endpoint: "tags",
		});
	}

	public static UpdateTags(request: UpdateTagsRequest) {
		return DataAdapter.PutUploadForm("tags", request);
	}

	public static GetDrillFolders(): Promise<TrainingDrillFolder[]> {
		return DataAdapter.Get<TrainingDrillFolder[]>({
			endpoint: "TrainingDrillFolders",
		});
	}

	public static GetDrillFolder(id: number): Promise<TrainingDrillFolder> {
		return DataAdapter.Get<TrainingDrillFolder>({
			endpoint: `TrainingDrillFolders/${id}`,
		});
	}

	public static GetCourseFolders(): Promise<TrainingFolder[]> {
		return DataAdapter.Get<TrainingFolder[]>({
			endpoint: "TrainingCourseFolders",
		});
	}

	public static GetCourse(courseId: number): Promise<TrainingCourse> {
		return DataAdapter.Get<TrainingCourse>({
			endpoint: `TrainingCourses/${courseId}`,
		});
	}

	public static GetCourses(): Promise<TrainingCourseSummary[]> {
		return DataAdapter.Get<TrainingCourseSummary[]>({
			endpoint: "TrainingCourses",
		});
	}

	public static UpdateTrainingDrillFolder(
		request: TrainingDrillFolderRequest,
		folderId: number,
	): Promise<void> {
		return DataAdapter.PutWithNoResponseBody(
			`TrainingDrillFolders/${folderId}`,
			request,
		);
	}

	public static DeleteTrainingDrillFolder(folderId: number) {
		return DataAdapter.Delete(`TrainingDrillFolders/${folderId}`);
	}

	public static CreateTrainingFolder(
		request: TrainingFolderRequest,
	): Promise<TrainingFolder> {
		return DataAdapter.Post<TrainingFolder>(`TrainingCourseFolders`, request);
	}

	public static UpdateTrainingFolder(
		request: TrainingFolderRequest,
		folderId: number,
	): Promise<TrainingFolder> {
		return DataAdapter.Put<TrainingFolder>(
			`TrainingCourseFolders/${folderId}`,
			request,
		);
	}

	public static DeleteTrainingFolder(folderId: number) {
		return DataAdapter.Delete(`TrainingCourseFolders/${folderId}`);
	}

	public static GetTrainingLink(linkId: number) {
		return DataAdapter.Get<TrainingLink>({
			endpoint: `TrainingLinks/${linkId}`,
		});
	}

	public static CreateTrainingLink(
		request: TrainingLinkRequest,
	): Promise<TrainingLink> {
		return DataAdapter.Post<TrainingLink>(`TrainingLinks`, request);
	}

	public static UpdateTrainingLink(
		request: TrainingLinkRequest,
		linkId: number,
	): Promise<TrainingLink> {
		return DataAdapter.Put<TrainingLink>(`TrainingLinks/${linkId}`, request);
	}

	public static DeleteTrainingLink(linkId: number) {
		return DataAdapter.Delete(`TrainingLinks/${linkId}`);
	}

	public static CreateTrainingCourse(
		request: TrainingCourseRequest,
	): Promise<TrainingCourse> {
		return DataAdapter.UploadForm<TrainingCourse>(`TrainingCourses`, request);
	}

	public static UpdateTrainingCourse(
		request: TrainingCourseRequest,
		courseId: number,
	): Promise<TrainingCourse> {
		return DataAdapter.PutUploadForm<TrainingCourse>(
			`TrainingCourses/${courseId}`,
			request,
		);
	}

	public static DeleteTrainingCourse(courseId: number) {
		return DataAdapter.Delete(`TrainingCourses/${courseId}`);
	}

	public static DeleteTrainingCourseFile(courseId: number, fileId: number) {
		return DataAdapter.Delete(`TrainingCourses/${courseId}/files/${fileId}`);
	}

	public static GetDrill(drillId: number | undefined) {
		return DataAdapter.Get<TrainingDrill>({
			endpoint: `TrainingDrills/${drillId}`,
		});
	}

	public static GetDrills() {
		return DataAdapter.Get<DrillSummary[]>({
			endpoint: "TrainingDrills",
		});
	}

	public static CreateTrainingDrill(
		request: TrainingDrillRequest,
	): Promise<void> {
		return DataAdapter.PostWithNoResponseBody(`TrainingDrills`, request);
	}

	public static UpdateTrainingDrill(
		drillId: number,
		request: TrainingDrillRequest,
	) {
		return DataAdapter.PutWithNoResponseBody(
			`TrainingDrills/${drillId}`,
			request,
		);
	}

	public static DeleteTrainingDrill(drillId: number) {
		return DataAdapter.Delete(`TrainingDrills/${drillId}`);
	}

	public static DeleteTrainingDrillFile(drillId: number, fileId: number) {
		return DataAdapter.Delete(`TrainingDrills/${drillId}/files/${fileId}`);
	}

	public static GetActiveSessionsByDrillId(drillId: number) {
		return DataAdapter.Get<TrainingDrillSession[]>({
			endpoint: `TrainingDrills/${drillId}/Sessions?status=Active`,
		});
	}

	public static GetArchivedSessionsByDrillId(drillId: number) {
		return DataAdapter.Get<TrainingDrillSession[]>({
			endpoint: `TrainingDrills/${drillId}/Sessions?status=Archived`,
		});
	}

	public static GetDrillSession(sessionId: number) {
		return DataAdapter.Get<TrainingDrillSession>({
			endpoint: `TrainingDrillSessions/${sessionId}`,
		});
	}

	public static GetDrillSessions(queryOptions: TrainingSessionsQueryOptions) {
		const endpoint = buildEndpointWithQueryParameters(
			"TrainingDrillSessions",
			queryOptions,
		);

		return DataAdapter.Get<TrainingDrillSessionSummary[]>({
			endpoint,
		});
	}

	public static GetCourseSessions(queryOptions: TrainingSessionsQueryOptions) {
		const endpoint = buildEndpointWithQueryParameters(
			"TrainingSessions",
			queryOptions,
		);

		return DataAdapter.Get<TrainingSessionSummary[]>({
			endpoint,
		});
	}

	public static GetCourseSession(sessionId: number) {
		return DataAdapter.Get<TrainingSession>({
			endpoint: `TrainingSessions/${sessionId}`,
		});
	}

	public static GetActiveSessionsByCourseId(courseId: number) {
		return DataAdapter.Get<TrainingSessionSummary[]>({
			endpoint: `TrainingCourses/${courseId}/Sessions?status=Active`,
		});
	}

	public static GetArchivedSessionsByCourseId(courseId: number) {
		return DataAdapter.Get<TrainingSessionSummary[]>({
			endpoint: `TrainingCourses/${courseId}/Sessions?status=Archived`,
		});
	}

	public static GetDrillSessionsByKpiId(
		kpiId: number,
		queryOptions: TrainingKpiStatisticsQueryOptions,
	) {
		const endpoint = buildEndpointWithQueryParameters(
			`TrainingKpis/${kpiId}/sessions`,
			queryOptions,
		);

		return DataAdapter.Get<TrainingDrillSessionSummaryWithKpiValue[]>({
			endpoint,
		});
	}

	public static CreateTrainingFile(
		request: TrainingFileRequest,
	): Promise<TrainingFile> {
		return DataAdapter.UploadForm<TrainingFile>(`TrainingFiles`, request);
	}

	public static CreateTrainingDrillSession(
		request: CreateTrainingDrillSessionRequest,
	): Promise<TrainingDrillSession> {
		return DataAdapter.Post<TrainingDrillSession>(
			`TrainingDrillSessions`,
			request,
		);
	}

	public static CreateTrainingSession(
		request: TrainingSessionRequest,
	): Promise<TrainingSession> {
		return DataAdapter.Post<TrainingSession>(`TrainingSessions`, request);
	}

	public static UpdateTrainingDrillSession(
		request: UpdateTrainingDrillSessionRequest,
	): Promise<TrainingDrillSession> {
		return DataAdapter.Put<TrainingDrillSession>(
			`TrainingDrillSessions/${request.Id}`,
			request,
		);
	}

	public static UpdateTrainingSession(
		sessionId: number,
		request: TrainingSessionRequest,
	): Promise<TrainingSession> {
		return DataAdapter.Put<TrainingSession>(
			`TrainingSessions/${sessionId}`,
			request,
		);
	}

	public static DeleteTrainingDrillSession(sessionId: number) {
		return DataAdapter.Delete(`TrainingDrillSessions/${sessionId}`);
	}

	public static DeleteTrainingSession(sessionId: number) {
		return DataAdapter.Delete(`TrainingSessions/${sessionId}`);
	}

	public static CompleteTrainingDrillSession(sessionId: number) {
		return DataAdapter.PatchWithNoPayload(
			`TrainingDrillSessions/${sessionId}/complete/`,
		);
	}

	public static CompleteTrainingSession(
		sessionId: number,
		request: CompleteTrainingSessionRequest,
	) {
		return DataAdapter.Patch(`TrainingSessions/${sessionId}/complete`, request);
	}

	public static ArchiveTrainingDrillSession(sessionId: number) {
		return DataAdapter.PatchWithNoPayload(
			`TrainingDrillSessions/${sessionId}/archive`,
		);
	}

	public static ArchiveTrainingSession(sessionId: number) {
		return DataAdapter.PatchWithNoPayload(
			`TrainingSessions/${sessionId}/archive`,
		);
	}

	public static DeleteTrainingSessionStudent(
		sessionId: number,
		studentId: number,
	) {
		return DataAdapter.Delete(
			`TrainingSessions/${sessionId}/students/${studentId}`,
		);
	}

	static GetStudentFailedReasons(): Promise<StudentFailedReason[]> {
		return DataAdapter.Get<StudentFailedReason[]>({
			endpoint: "StudentFailedReasons",
		});
	}

	static PostStudentFailedReason(
		request: StudentFailedReasonRequest,
	): Promise<StudentFailedReason> {
		return DataAdapter.Post<StudentFailedReason>(
			"StudentFailedReasons",
			request,
		);
	}

	static PutStudentFailedReason(
		id: number,
		request: StudentFailedReasonRequest,
	): Promise<StudentFailedReason> {
		return DataAdapter.Put<StudentFailedReason>(
			`StudentFailedReasons/${id}`,
			request,
		);
	}

	static DeleteStudentFailedReason(id: number) {
		return DataAdapter.Delete(`StudentFailedReasons/${id}`);
	}

	static GetKpis(): Promise<TrainingKpi[]> {
		return DataAdapter.Get<TrainingKpi[]>({
			endpoint: "TrainingKpis",
		});
	}

	static GetTrainingUsersWithMissedSessions(): Promise<TrainingUsersResponse> {
		return DataAdapter.Get<TrainingUsersResponse>({
			endpoint: "TrainingUsers",
		});
	}

	static PostKpi(request: TrainingKpiRequest): Promise<TrainingKpi> {
		return DataAdapter.Post<TrainingKpi>("TrainingKpis", request);
	}

	static PutKpi(id: number, request: TrainingKpiRequest): Promise<TrainingKpi> {
		return DataAdapter.Put<TrainingKpi>(`TrainingKpis/${id}`, request);
	}

	static DeleteKpi(id: number) {
		return DataAdapter.Delete(`TrainingKpis/${id}`);
	}

	static GetTrainingKpi(kpiId: number): Promise<TrainingKpi> {
		return DataAdapter.Get<TrainingKpi>({
			endpoint: `TrainingKpis/${kpiId}`,
		});
	}

	static GetTrainingKpiStatistics(
		queryOptions: TrainingKpiStatisticsQueryOptions,
	): Promise<TrainingKpiStatistic[]> {
		const endpoint = buildEndpointWithQueryParameters(
			"TrainingKpis/Statistics",
			queryOptions,
		);

		return DataAdapter.Get<TrainingKpiStatistic[]>({
			endpoint,
		});
	}

	public static UpdateAzureAdGroups() {
		return DataAdapter.PutWithNoPayload(`User/AzureAd`);
	}

	public static UpdateAzureAdGlobalAdministrators() {
		return DataAdapter.PutWithNoPayload(`User/AzureAd/GlobalAdministrators`);
	}

	public static GetUserSummariesByCurrentCompany(): Promise<UserSummary[]> {
		return DataAdapter.Get<UserSummary[]>({
			endpoint: "user/FromCurrentOrganization",
		});
	}
	public static GetNodeVersionsComparison(nodeId: number, procedureId: number) {
		return DataAdapter.Get<NodeVersionComparison>({
			endpoint: `nodes/${nodeId}/Compare?procedureId=${procedureId}`,
		});
	}

	public static UpdateChecklist(
		request: any,
		checklistId: number,
		procedureId: number,
	) {
		return DataAdapter.Patch<any>(
			`Checklist/${checklistId}/UpdateManualChecklist`,
			request,
		);
	}

	public static UploadManualChecklistStepImage(request: {
		File: File;
		procedureId: number;
	}): Promise<FileUpload> {
		return DataAdapter.UploadForm<FileUpload>(
			"FileUpload/ManualChecklistImage",
			request,
		);
	}

	static GetManualMeasurementPoint(id: number) {
		return DataAdapter.Get<ManualMeasurementPointResponse>({
			endpoint: `MeasurementPoints/${id}`,
		});
	}

	static PostManualMeasurementPoint(request: ManualMeasurementPointRequest) {
		return DataAdapter.Post<ManualMeasurementPointResponse>(
			"MeasurementPoints",
			request,
		);
	}

	static PutManualMeasurementPoint(request: ManualMeasurementPointRequest) {
		return DataAdapter.Put<ManualMeasurementPointResponse>(
			`MeasurementPoints/${request.Id}`,
			request,
		);
	}

	static DeleteManualMeasurementPoint(id: number): Promise<void> {
		return DataAdapter.Delete(`MeasurementPoints/${id}`);
	}

	static GetManualMeasurementGroups() {
		return DataAdapter.Get<ManualMeasurementPointGroup[]>({
			endpoint: "MeasurementGroups",
		});
	}

	static GetManualMeasurementGroup(id: number) {
		return DataAdapter.Get<ManualMeasurementPointGroup>({
			endpoint: `MeasurementGroups/${id}`,
		});
	}

	static PostManualMeasurementGroup(
		request: CreateManualMeasurementGroupRequest,
	) {
		return DataAdapter.Post<ManualMeasurementPointGroup>(
			"MeasurementGroups",
			request,
		);
	}

	static PutManualMeasurementGroup(
		request: UpdateManualMeasurementGroupRequest,
	) {
		return DataAdapter.Put<ManualMeasurementPointGroup>(
			`MeasurementGroups/${request.Id}`,
			request,
		);
	}

	static DeleteManualMeasurementGroup(id: number) {
		return DataAdapter.Delete(`MeasurementGroups/${id}`);
	}
}

export const buildEndpointWithQueryParameters = (
	endpoint: string,
	queryOptions: {},
) => {
	const activeQueryOptions = Object.fromEntries(
		Object.entries(queryOptions).filter(([key, value]) => value),
	);
	const params = new URLSearchParams(activeQueryOptions);

	if (params.size > 0) {
		endpoint += `?${params.toString()}`;
	}

	return endpoint;
};
